/**
 * Merchant Payment Terms — required before linking Stripe or PayPal payment options.
 * Bump MERCHANT_PAYMENT_TERMS_VERSION when terms materially change (sellers must re-accept).
 */

export const MERCHANT_PAYMENT_TERMS_VERSION = '2026-05-29-direct-v1';

/** Shown in UI when describing what acceptance unlocks */
export const MERCHANT_PAYMENT_LINKING_LABEL = 'Stripe or PayPal';

export const BOOKAPT_TERMS_URL = 'https://bookapt.io/terms.html';
export const BOOKAPT_PRIVACY_URL = 'https://bookapt.io/privacy.html';

export type MerchantTermsCheckboxId =
  | 'merchant_of_record'
  | 'connected_account_payouts'
  | 'no_transaction_commission'
  | 'refunds_disputes_responsibility'
  | 'accurate_payment_account'
  | 'bookapt_platform_role'
  | 'indemnity_liability'
  | 'terms_of_service'
  | 'privacy_policy'
  | 'merchant_payment_addendum';

export interface MerchantTermsCheckboxItem {
  id: MerchantTermsCheckboxId;
  label: string;
  /** If set, label is plain text and this link is shown after it */
  linkLabel?: string;
  linkUrl?: string;
}

export const MERCHANT_TERMS_CHECKBOXES: MerchantTermsCheckboxItem[] = [
  {
    id: 'merchant_of_record',
    label:
      'I understand that I am the seller and merchant of record for services I provide. I — not BookAPT — sell services to my customers.',
  },
  {
    id: 'connected_account_payouts',
    label:
      'I understand that when I link Stripe or PayPal, customers pay me directly on my Stripe or PayPal account (e.g. Stripe Payment Link or PayPal.me). BookAPT does not process, hold, or route those funds.',
  },
  {
    id: 'no_transaction_commission',
    label:
      'I understand that BookAPT does not charge a per-transaction commission on appointment payments customers make to me on Stripe or PayPal. Standard fees from Stripe or PayPal still apply.',
  },
  {
    id: 'refunds_disputes_responsibility',
    label:
      'I am solely responsible for refunds, cancellations, customer disputes, and chargebacks related to my services, and for handling them in my Stripe or PayPal account and with my customers.',
  },
  {
    id: 'accurate_payment_account',
    label:
      'I will keep my Stripe and PayPal account information and payment links accurate and in good standing, and I will not use BookAPT for prohibited or illegal activity.',
  },
  {
    id: 'bookapt_platform_role',
    label:
      'I understand BookAPT provides booking and business software. BookAPT may display my payment links and payment status; BookAPT is not the seller of my services and does not process customer payments.',
  },
  {
    id: 'indemnity_liability',
    label:
      'I agree that I am responsible for losses, claims, or negative balances arising from my payment activity, and I will indemnify BookAPT to the extent permitted by law for such matters tied to my account and services.',
  },
  {
    id: 'terms_of_service',
    label: 'I have read and agree to the ',
    linkLabel: 'BookAPT Terms of Service',
    linkUrl: BOOKAPT_TERMS_URL,
  },
  {
    id: 'privacy_policy',
    label: 'I have read and agree to the ',
    linkLabel: 'Privacy Policy',
    linkUrl: BOOKAPT_PRIVACY_URL,
  },
  {
    id: 'merchant_payment_addendum',
    label:
      'I have read and agree to the Merchant Payment Terms summary shown above, in addition to the BookAPT Terms of Service.',
  },
];

export const REQUIRED_MERCHANT_TERMS_IDS: MerchantTermsCheckboxId[] =
  MERCHANT_TERMS_CHECKBOXES.map((c) => c.id);

/** Shown above individual checkboxes — merchant payment addendum summary */
export const MERCHANT_PAYMENT_ADDENDUM_SUMMARY = `Merchant Payment Terms (Summary)

Effective as of the version shown when you link a payment option.

1. Your role. You operate an independent business. You are the merchant of record for payments for your services.

2. Payment flow (Stripe and PayPal). When you link Stripe or PayPal in BookAPT, you provide a payment link or PayPal.me address so customers can pay you directly. BookAPT does not process, hold, or settle those payments. BookAPT does not take a per-transaction commission on money paid to you on Stripe or PayPal.

3. Your obligations. You must comply with applicable laws, tax obligations, and Stripe’s and PayPal’s terms. You handle refunds, cancellations, disputes, and chargebacks with your customers in your own Stripe or PayPal account.

4. BookAPT’s role. BookAPT provides software to manage bookings and related features. BookAPT is not a party to the sale between you and your customer.

5. Confirmation. You are responsible for confirming in BookAPT when a customer has paid you outside the app (unless a future in-app payment feature applies).

6. Suspension. BookAPT may suspend payment-related features if we detect fraud, abuse, or violation of our terms or applicable law.

7. Changes. If these terms change materially, you may be asked to accept the updated version before continuing to use payment features.

This summary is provided for convenience. The checkbox acknowledgments and BookAPT Terms of Service govern your use of the platform.`;
